import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, filter, map, Observable, Subject, Subscription, tap, throwError } from 'rxjs';
import { BackendService } from 'src/app/core/services/backend.service';
import { LangSetterService } from 'src/app/core/services/lang-setter.service';
import { TranslationsOverrideService } from 'src/app/core/services/translations-override.service';
import { Company } from 'src/app/models/admin-models/views/Company';
import { INavigationPermission } from 'src/app/models/enums/StepEnum';
import { CartItem } from '../../custom-steps/shopping-cart-display-step/models/cartItem';
import { Step, StepDTO } from '../../step/models/step';
import { StepData } from '../../step/services/step.service';

@Injectable()
export class StepDataBridgeService {
  //public rootStep?: Step;
  public currentRoute?: string;

  private _rootStep: BehaviorSubject<Step | undefined> = new BehaviorSubject<Step | undefined>(undefined);
  public rootStep$: Observable<Step | undefined> = this._rootStep.asObservable();

  private _nextStepRequest: Subject<boolean> = new Subject<boolean>();
  public nextStepRequest$: Observable<boolean> = this._nextStepRequest.asObservable();

  private _previousStepRequested: Subject<boolean> = new Subject<boolean>();
  public previousStepRequested$: Observable<boolean> = this._previousStepRequested.asObservable();

  private _allSteps: BehaviorSubject<Step[]> = new BehaviorSubject<Step[]>([]);
  public allSteps$: Observable<Step[]> = this._allSteps.asObservable();

  private _backEnabled: BehaviorSubject<INavigationPermission> = new BehaviorSubject<INavigationPermission>({
    isEnabled: false,
  });

  protected _companySummary: BehaviorSubject<Company | undefined> = new BehaviorSubject<Company | undefined>(undefined);

  public companySummary$: Observable<Company | undefined> = this._companySummary.asObservable();

  private _cartItems: BehaviorSubject<CartItem[] | undefined> = new BehaviorSubject<CartItem[] | undefined>(undefined);
  public cartItems$: Observable<CartItem[] | undefined> = this._cartItems.asObservable();

  public backEnabled$: Observable<INavigationPermission> = this._backEnabled.asObservable();

  private _forwardEnabled: BehaviorSubject<INavigationPermission> = new BehaviorSubject<INavigationPermission>({
    isEnabled: false,
  });

  public forwardEnabled$: Observable<INavigationPermission> = this._forwardEnabled.asObservable();

  private _stepData: BehaviorSubject<StepData | undefined> = new BehaviorSubject<StepData | undefined>(undefined);
  public stepData$: Observable<StepData | undefined> = this._stepData.asObservable();

  private _stepDataSubscription?: Subscription;

  private _showShoppingCartButton: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showShoppingCartButton$: Observable<boolean> = this._showShoppingCartButton.asObservable();

  constructor(
    private http: HttpClient,
    private backendService: BackendService,
    private translate: TranslateService,
    private langSetterService: LangSetterService,
    private translationsOverrideService: TranslationsOverrideService,
  ) {
    this._subscribeForCompanyChange();
  }

  private _subscribeForCompanyChange() {
    this.companySummary$
      .pipe(
        filter((company) => company !== undefined),
        tap((company) => {
          this.langSetterService.setLanguagesFromCompany(company!);
          this.translationsOverrideService.companyChanged(company!);
        }),
      )
      .subscribe();
  }

  public publishShowShoppingCartButton(show: boolean) {
    this._showShoppingCartButton.next(show);
  }

  public publishStepData(stepData$: Observable<StepData | undefined>) {
    this._stepDataSubscription?.unsubscribe();
    this._stepDataSubscription = stepData$.subscribe((stepData) => this._stepData.next(stepData));
  }

  public publishForwardAllowed(isAllowed: INavigationPermission) {
    this._forwardEnabled.next(isAllowed);
  }

  public publishBackAllowed(isAllowed: INavigationPermission) {
    this._backEnabled.next(isAllowed);
  }

  public requestNextStep() {
    this._nextStepRequest.next(true);
  }

  public requestPreviousStep() {
    this._previousStepRequested.next(true);
  }

  public syncStepsBack() {
    this._previousStepRequested.next(false);
  }

  public syncStepsForward() {
    this._nextStepRequest.next(false);
  }

  public setRootStep(step: Step) {
    this._rootStep.next(step);
  }

  public getCurrentRootStep() {
    return this._rootStep.value;
  }

  public getAllSteps() {
    return this._allSteps.value;
  }

  public publishCompanySummary(company: Company | undefined) {
    if (company === undefined) {
      this._companySummary.next(company);
      return;
    }
    if (company.id !== this._companySummary.value?.id) this._companySummary.next(company);
  }

  public publishCartItems(cartItems: CartItem[] | undefined) {
    this._cartItems.next(cartItems);
  }

  public loadRootStep$(): Observable<Step> {
    if (this._rootStep.value !== undefined)
      return this.rootStep$.pipe(
        filter((step): step is Step => step !== undefined), // type guard to fix TS error
      );

    return this.http.get<StepDTO>('/assets/rootStep.json').pipe(
      map((stepDTO) => Step.fromDTO(stepDTO)),
      tap((step) => {
        this._rootStep.next(step);
      }),

      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }

  public loadAllSteps$(): Observable<Step[]> {
    if (this._allSteps.value.length !== 0) return this.allSteps$;

    return this.backendService.getAllStepsFlattened().pipe(
      map((stepDTOs) => {
        return stepDTOs.map((stepDTO) => Step.fromDTO(stepDTO));
      }),
      tap((steps) => {
        this._allSteps.next(steps);
      }),

      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }
}
